import type { CalendarClickType, AnnualCalendarProps as Props, YYYYMMDD } from '../../typings'

import { endOfYear, format, startOfYear } from 'date-fns/esm'
import { useCallback } from 'react'

import { Month } from '../../components/Month'
import { MonthNavigator } from '../../components/MonthNavigator'
import { useMonthNavigatorProps } from '../../hooks/useMonthNavigatorProps'
import { useMonths } from '../../hooks/useMonths'
import { parseDate } from '../../utils/parseDate'

import { Stack } from '@mui/material'
import { useAnnualDays } from './useAnnualDays'
import { useSelectionData } from './useSelectionData'

export function Annual(props: Props) {
  const {
    today,
    dotColor,
    onChange,
    selection,
    disabledDays,
    referenceDate,
    highlightedDays,
    setReferenceDate,
    firstNonArchivedDay,
    showNeighboringMonth,
    allowAutoMonthHeight,
  } = props

  const todayDate = parseDate(today)

  const minDate = format(startOfYear(todayDate), 'yyyyMMdd')
  const maxDate = format(endOfYear(todayDate), 'yyyyMMdd')

  const months = useMonths(referenceDate, showNeighboringMonth, 1, minDate, maxDate)

  const selectionData = useSelectionData(selection)

  const daysByMonth = useAnnualDays({
    selection: selectionData,
    today,
    firstNonArchivedDay,
    highlightedDays,
    disabledDays,
    months,
  })

  const monthNavigatorProps = useMonthNavigatorProps({
    referenceDate,
    setReferenceDate,
    months: 1,
    minDateAsString: minDate,
    maxDateAsString: maxDate,
    labelFormat: 'month',
  })

  const handleOnClick = useCallback(
    (day?: YYYYMMDD, type: CalendarClickType = 'click') => {
      if (day) {
        const dayDate = parseDate(day)
        onChange(dayDate, selection, type)
      }
    },
    [onChange, selection],
  )

  return (
    <Stack direction="column" height="auto" alignItems="center" flexShrink={0}>
      <MonthNavigator {...monthNavigatorProps} />
      <Stack direction="row" width="auto">
        {months.map(
          (month, index) =>
            daysByMonth[month.id] && (
              <Month
                index={index}
                key={month.id}
                dotColor={dotColor}
                onClick={handleOnClick}
                days={daysByMonth[month.id]}
                allowAutoMonthHeight={allowAutoMonthHeight}
              />
            ),
        )}
      </Stack>
    </Stack>
  )
}
